import { useGoogleLogin } from "@react-oauth/google";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import Modal from "~/components/ui/modal/Modal";
import { messageFromError } from "~/helpers/messageFromError";
import { tryCatch } from "~/helpers/tryCatch";
import { QUERY_KEYS } from "~/pages/GoogleDashboardPage/constants";

import { apiClient } from "../../../../api/ApiClient";
import { Icon } from "../../../../assets/icons/Icon";
import Button from "../../../mantine/button/Button";
import styles from "./AddAccountModal.module.scss";

interface AddAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddAccountModal = ({ isOpen, onClose }: AddAccountModalProps) => {
  const [isLoading, setLoading] = useState(false);

  const queryClient = useQueryClient();

  const { mutateAsync: createUserIdentity } = useMutation({
    mutationFn: async (code: string) => {
      return await apiClient.userIdentities.userIdentitiesCreate({
        code: code,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.USER_IDENTITIES,
      });
    },
  });

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const result = await tryCatch(createUserIdentity(tokenResponse.code));

        if (result.error) {
          enqueueSnackbar(messageFromError(result.error), {
            variant: "error",
            persist: true,
          });

          return;
        }

        enqueueSnackbar("Google analytics account connected", {
          variant: "success",
        });
        onClose();
      } catch (e) {
        enqueueSnackbar(messageFromError(e), {
          variant: "error",
          persist: true,
        });
      } finally {
        setLoading(false);
      }
    },
    onError: (error) => {
      setLoading(false);
      enqueueSnackbar(
        `Google login error occurred: ${error.error_description} (${error.error})`,
        {
          variant: "error",
          persist: true,
        },
      );
    },
    onNonOAuthError: () => {
      // cases when user closed popup/iframe, blocked by browser, etc.
      setLoading(false);
    },
    scope:
      "https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/webmasters.readonly",
    flow: "auth-code",
  });

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      modalLabel={<h3>Connect Google Analytics Account</h3>}
      width="32rem"
    >
      <div className={styles.container}>
        <div className={styles.description}>
          <strong>This will grant access to:</strong>
          <ul>
            <li>Read your Google Analytics data</li>
            <li>View Analytics account and property information</li>
            <li>Access website traffic and performance metrics</li>
          </ul>
          <em>
            We only access data needed for website insights. No personal
            information is collected.
          </em>
        </div>

        <div className={styles.buttonContainer}>
          <Button
            onClick={() => {
              setLoading(true);
              login();
            }}
            loading={isLoading}
            variant="outlined"
            className={styles.googleButton}
          >
            <Icon iconName="GoogleIcon" />
            Log in with Google
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AddAccountModal;
